import { useMemo } from 'react';
import type { Task } from '@/common/api/models/task';
import type { Module } from '@/student/types';
import { useTenantEventKinds } from '@/common/context/TenantSettingsStore';
import { taskRequiresEvaluation } from '@/common/utils/taskEvaluationUtils';

export interface GradeReportData {
  evaluatedTasks: Task[];
  oralExamTasks: Task[];
  delayedFeedbackMcqs: Task[];
  transferTasks: Task[];
  regularTasks: Task[];
  essayTasks: Task[];
  showEssayInsights: boolean;
}

export const useGradeReportData = (_module: Module, tasks: Task[]): GradeReportData => {
  const { eventKinds } = useTenantEventKinds();

  const evaluatedTasks = useMemo(
    () => tasks?.filter((task) => taskRequiresEvaluation(task, eventKinds)) || [],
    [tasks, eventKinds],
  );

  const delayedFeedbackMcqs = useMemo(
    () => tasks?.filter((task) => {
      if (task?.type !== 'multiple_choice' || !task?.submission) return false;
      const mcqConfig = 'mcqConfig' in task ? task.mcqConfig : undefined;
      return mcqConfig?.showImmediateFeedback === false;
    }) || [],
    [tasks],
  );

  const { oralExamTasks, transferTasks, regularTasks } = useMemo(() => {
    const oral: Task[] = [];
    const transfer: Task[] = [];
    const regular: Task[] = [];

    const isOralExam = (task: Task) => task?.type === 'oral_exam';
    const isMcq = (task: Task) => task?.type === 'multiple_choice';

    evaluatedTasks.forEach((task) => {
      if (isMcq(task)) return;

      if (isOralExam(task)) {
        oral.push(task);
        return;
      }

      if (task?.gradingSystem === 'full_evaluation') {
        transfer.push(task);
      } else {
        regular.push(task);
      }
    });

    return { oralExamTasks: oral, transferTasks: transfer, regularTasks: regular };
  }, [evaluatedTasks]);

  const essayTasks = useMemo(
    () => [...transferTasks, ...regularTasks],
    [transferTasks, regularTasks],
  );

  const showEssayInsights = essayTasks.length > 0 && oralExamTasks.length === 0;

  return {
    evaluatedTasks,
    oralExamTasks,
    delayedFeedbackMcqs,
    transferTasks,
    regularTasks,
    essayTasks,
    showEssayInsights,
  };
};
